import React, { useMemo } from "react";
import { Menu, Transition } from "@headlessui/react";
import { Chip } from "@scripts/react/components/Chip";
import { TooltipContainer } from "@scripts/react/components/Tooltip";
import RadioButton from "@scripts/react/components/RadioButton";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { Visibility } from "@rapid/store/slices/rapidDataSlice";
import { useAuth } from "@rapid/contexts/AuthContext";
import { useUpgradeModal } from "@rapid/contexts/UpgradeModalContext";
import { useVisibilityThroughWorkspace } from "@rapid/hooks/useVisibilityThroughWorkspace";
import { useWorkspaces } from "@rapid/queries/workspaceQueries";

interface DataVisibilitySwitcherProps {}

export const DataVisibilitySwitcher: React.FC<DataVisibilitySwitcherProps> = () => {
    const { isLoading: loadingWorkspaces } = useWorkspaces();
    const { requireLogin, user } = useAuth();
    const { openUpgradeModal } = useUpgradeModal();

    const visibilityThroughWorkspace = useVisibilityThroughWorkspace();
    const visibility = visibilityThroughWorkspace;

    const onVisibilitySelect = (selectedVisibility: Visibility) => {
        // NOTE: Not possible to change visibility from this component, it is derived from the workspace usage

        const shouldUpgrade = visibility === "public" && selectedVisibility === "private";

        if (shouldUpgrade) {
            if (user) {
                openUpgradeModal("rapid-private-data");
            } else {
                requireLogin();
            }
        }
    };

    const visibilityOptions = [
        {
            id: "public",
            label: "Public",
            icon: "far fa-globe",
            description: "For open source",
            disabled: visibility === "private"
        },
        { id: "private", label: "Private", icon: "far fa-lock", description: "For teams" }
    ];

    const selectedOption =
        visibilityOptions.find((option) => option.id === visibility) || visibilityOptions[0];

    return (
        <>
            <Menu as="div" className="relative inline-block text-left">
                {({ open }) => (
                    <>
                        <TooltipContainer
                            placement="bottom"
                            type="titleOnly"
                            title={
                                visibility === "public"
                                    ? "Change Data Visibility"
                                    : "You have private data through your paid plan"
                            }
                            disabled={open || loadingWorkspaces}
                            customButton={
                                <Menu.Button as="div" className="cursor-pointer">
                                    <Chip type="text" color="white" className="hover:bg-gray-50">
                                        <i
                                            className={twClassNames(
                                                selectedOption.icon,
                                                "text-secondary"
                                            )}
                                        />
                                        {visibility === "public" ? "Public" : "Private"}
                                    </Chip>
                                </Menu.Button>
                            }
                        />

                        <Transition
                            as={React.Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                            unmount={false}
                        >
                            <Menu.Items
                                unmount={false}
                                className="absolute right-0 z-50 mt-2 w-72 origin-top-right rounded-xl border bg-white p-1 text-primary transition duration-100 ease-out focus:outline-none"
                            >
                                {visibilityOptions.map((option) => {
                                    const shouldShowUpgrade = visibility === "public";
                                    return (
                                        <Menu.Item key={option.id} as="div">
                                            <button
                                                className={twClassNames(
                                                    "border-gray group flex w-full scale-100 items-center justify-between rounded-lg px-3 py-2 font-sans text-sm font-medium transition duration-200 hover:bg-gray-200 focus:outline-none",
                                                    visibility === option.id
                                                        ? "bg-tint text-brand"
                                                        : "text-secondary",
                                                    option.disabled && "cursor-not-allowed"
                                                )}
                                                disabled={option.disabled}
                                                onClick={() => {
                                                    onVisibilitySelect(option.id as Visibility);
                                                }}
                                            >
                                                {/* Left side: Radio button, label, and icon */}
                                                <div className="flex items-center gap-2">
                                                    <RadioButton
                                                        checked={visibility === option.id}
                                                        disabled={option.disabled}
                                                        onChange={() => null}
                                                        className="flex-shrink-0"
                                                    />
                                                    {option.label}
                                                    <i
                                                        className={twClassNames(
                                                            option.icon,
                                                            visibility === option.id
                                                                ? "text-brand"
                                                                : "text-secondary"
                                                        )}
                                                    />
                                                </div>

                                                {/* Right side: Description chip */}
                                                <Chip
                                                    type="text"
                                                    color={
                                                        visibility === option.id ? "purple" : "gray"
                                                    }
                                                    className="flex-shrink-0 text-xs"
                                                >
                                                    {option.description}
                                                </Chip>
                                            </button>
                                        </Menu.Item>
                                    );
                                })}
                            </Menu.Items>
                        </Transition>
                    </>
                )}
            </Menu>
        </>
    );
};
